/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Inject, Injectable} from '@angular/core';
import * as fileSaver from 'file-saver-es';
import {Observable} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {Environment} from '../interfaces/Environment';
import {Picture} from '../interfaces/Picture';
import {BaseService} from './base.service';
import {CompoApiService} from './compo-api.service';

@Injectable({
  providedIn: 'root',
})
export class FileService extends BaseService {
  constructor(@Inject('environment') private readonly environment: Environment,
              private readonly apiService: CompoApiService
  ) {
    super();
  }

  static saveAs(data: Blob, fileName: string, mimeType: string = 'application/octet-stream'): void {
    const blob = new Blob([data], {type: mimeType});
    fileSaver.saveAs(blob, fileName);
  }

  getImageUri(picture: Picture, width?: number | string): string {
    const dir = width === undefined ? this.environment.originalsImagesFolder : width;
    return this.environment.serverPicturesPath + '/' + dir + '/' + picture.name + '.' + picture.type;
  }

  getImageSrc(picture: string, width?: number | string): string {
    const dir = width === undefined ? this.environment.originalsImagesFolder : width;
    return this.environment.serverPicturesPath + '/' + dir + '/' + picture;
  }

  getProductImageSrc(
    picture: string,
    picturesSource?: 'DEFAULT' | 'DAM' | null,
    width?: number | string,
    showcaseHash?: string | null,
  ): string {
    if (picturesSource === 'DAM' && showcaseHash) {
      return this.getShowcaseDamPictureUri(showcaseHash, picture);
    }
    if (picturesSource === 'DAM') {
      return this.getPublicDocumentUri(picture);
    }
    return this.getImageSrc(picture, width);
  }

  getProductThumbnailSrc(
    product: {picture?: string; pictures?: string[]; picturesSource?: 'DEFAULT' | 'DAM'} | null | undefined,
    width: number | string = 200,
    showcaseHash?: string | null,
  ): string | null {
    if (!product) {
      return null;
    }
    if (product.picturesSource === 'DAM') {
      const first = product.pictures?.[0] ?? product.picture;
      if (!first) return null;
      return showcaseHash ? this.getShowcaseDamPictureUri(showcaseHash, first) : this.getPublicDocumentUri(first);
    }
    const pic = product.picture;
    return pic && pic !== '.' ? this.getImageSrc(pic, width) : null;
  }

  /**
   * URL DAM-картинки витрины через стриминговый backend-эндпоинт.
   * Заменяет прямое обращение к `/documents/...`, которое 404-ит на проде
   * (DAM-pipeline пишет в private-root, а nginx раздаёт только public-root).
   * Эндпоинт публичный (без Authorization), inline (без Content-Disposition),
   * с ETag/Cache-Control — `<img src>` работает напрямую и кешируется браузером.
   */
  getShowcaseDamPictureUri(showcaseHash: string, picture: string): string {
    return this.environment.serverApi + '/showcase/' + encodeURIComponent(showcaseHash) + '/dam/picture/' + picture;
  }

  getPublicDocumentUri(fileName: string): string {
    return this.environment.serverDocumentsPath + '/' + fileName;
  }

  /**
   * Абсолютный URL стримингового скачивания документа витрины.
   * `downloadUrl` приходит host-relative с api-префиксом (`/api/v1/showcase/{hash}/download?...`),
   * поэтому добавляем только origin (без `/api/v1`) — вытаскиваем его из `serverApi`.
   * Эндпоинт публичный, отдаёт `Content-Disposition: attachment` — `<a href download>` достаточно.
   */
  getShowcaseDownloadUri(downloadUrl: string): string {
    if (/^https?:\/\//i.test(downloadUrl)) {
      return downloadUrl;
    }
    return this.apiOrigin() + downloadUrl;
  }

  private apiOrigin(): string {
    const api = this.environment.serverApi || '';
    try {
      return new URL(api).origin;
    } catch {
      return api.replace(/\/api\/v\d+\/?$/i, '');
    }
  }

  downloadUrl(url: string): Observable<Blob> {
    return this.apiService.downloadUrl(url).pipe(catchError(err => this.handleError(err)), take(1));
  }

  downloadPostUrl<T>(url: string, data: T): Observable<Blob> {
    return this.apiService.downloadPostUrl(url, data).pipe(catchError(err => this.handleError(err)));
  }

}
